<script setup>
import { useUserStore } from "@/store/user-store";
import { Chip, Column, DataTable, Dialog, Rating } from "primevue";
import { onMounted, ref, watch, watchEffect, computed } from "vue";
import AnLoader from "../common/AnLoader.vue";
import AnButton from "../common/AnButton.vue";
import {
  chartColors,
  gridHighlightPlugin,
  lineChartOptions,
  pieChartOptions,
} from "@/services/constant-services";
import Chart from "primevue/chart";
import { convertToInr } from "@/services/common-services";
import NewsSection from "@/sections/dashboard/NewsSection.vue";

const props = defineProps({
  isStockDialogOpen: {
    type: Boolean,
    default: false,
  },
  stock: {
    type: Object,
    default: {},
  },
});

const emit = defineEmits(["update:isStockDialogOpen"]);

const dialogVisible = ref(props.isStockDialogOpen);
const dataLoading = ref(false);
const stockData = ref(null);
const loadError = ref(false);
const quarterlyPerformance = ref(null);
const news = ref(null);
const profitLoss = ref(null);
const generalData = ref(null);
const balanceSheet = ref(null);
const cashFlows = ref(null);
const shareholdingPattern = ref(null);
const store = useUserStore();
const isExpanded = ref(false);
const maxLength = 300;

const truncatedText = computed(() => {
  if (!stockData.value) return "";
  const text = stockData.value.aboutCompany || "";
  return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
});

const showToggle = computed(() => {
  const text = stockData.value?.aboutCompany || "";
  return text.length > maxLength;
});

// Price & P/E time-series: minimal, responsive viewer
const pricePERaw = ref([]);
const timeframe = ref('1Y');
const timeframeOptions = ['1M', '3M', '6M', '1Y', '3Y', 'MAX'];
const pricePEChartData = ref({ labels: [], datasets: [] });
const pricePEChartOptions = computed(() => {
  return {
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    },
    interaction: { mode: 'index', intersect: false },
    scales: {
      y1: {
        type: 'linear',
        position: 'left',
        title: { display: true, text: 'Price (INR)' },
        grid: { drawOnChartArea: true }
      },
      y2: {
        type: 'linear',
        position: 'right',
        title: { display: true, text: 'P/E Ratio' },
        grid: { drawOnChartArea: false }
      },
      x: {
        ticks: { maxTicksLimit: 8 },
        grid: { display: false }
      }
    }
  };
});

const isMobile = ref(window.innerWidth <= 768);

const updateIsMobile = () => (isMobile.value = window.innerWidth <= 768);

onMounted(() => {
  window.addEventListener('resize', updateIsMobile);
});

const parsePricePE = (arr) =>
  (arr || []).map((it) => ({
    date: new Date(it.Date),
    close: Number(it.ClosePrice),
    pe: Number(it.PERatio)
  }));

const getFilteredPricePE = (tf) => {
  const raw = parsePricePE(stockData.value?.pricePE || []);
  if (raw.length === 0) return [];
  raw.sort((a, b) => a.date - b.date);
  if (tf === 'MAX') return raw;

  const daysMap = { '1M': 30, '3M': 90, '6M': 180, '1Y': 365, '3Y': 365 * 3 };
  const days = daysMap[tf] || 365;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return raw.filter((r) => r.date >= cutoff);
};

const buildPricePEChart = (tf = timeframe.value) => {
  const dataArr = getFilteredPricePE(tf);
  pricePEChartData.value.labels = dataArr.map((d) => d.date.toLocaleDateString());
  pricePEChartData.value.datasets = [
    {
      label: 'Close Price',
      data: dataArr.map((d) => d.close),
      borderColor: chartColors[0] || '#3b82f6',
      backgroundColor: 'rgba(59,130,246,0.08)',
      tension: 0.2,
      yAxisID: 'y1',
      pointRadius: isMobile.value ? 0 : 2,
      borderWidth: 2
    },
    {
      label: 'P/E Ratio',
      data: dataArr.map((d) => d.pe),
      borderColor: chartColors[1] || '#10b981',
      backgroundColor: 'rgba(16,185,129,0.06)',
      tension: 0.2,
      yAxisID: 'y2',
      pointRadius: isMobile.value ? 0 : 2,
      borderWidth: 2
    }
  ];
};

// Rebuild chart when stockData or timeframe changes
watch([() => stockData.value?.pricePE, timeframe], () => {
  buildPricePEChart(timeframe.value);
});

const loadStockData = async () => {
  dataLoading.value = true;
  loadError.value = false;
  try {
    const response = await store.getStocksData(props.stock.stockName);
    stockData.value = response;

    // Check if data is actually present
    if (!stockData.value || Object.keys(stockData.value).length === 0) {
      loadError.value = true;
      stockData.value = null;
    }
  } catch (error) {
    console.error("Error loading stock data:", error);
    loadError.value = true;
    stockData.value = null;
    // store.fireAlert("Error", error.message);
  } finally {
    dataLoading.value = false;
  }
};

const navigateToSupport = () => {
  emit('update:isStockDialogOpen', false);
  store.setTabView('Operations Support');
};

watchEffect(() => {
  if (!stockData.value) return;

  generalData.value = [
    {
      key: "Market cap",
      value: "₹ " + stockData.value.mainSection.marketCap + " Cr.",
    },
    {
      key: "Current Price",
      value: "₹ " + stockData.value.mainSection.currentPrice,
    },
    {
      key: "Stock P/E",
      value: stockData.value.mainSection.priceEarningsRatio,
    },
    {
      key: "ROCE",
      value: stockData.value.mainSection.ROCE + " %",
      //Add tooltip
      tooltip: "",
    },
    {
      key: "ROE",
      value: stockData.value.mainSection.ROE + " %",
    },
    {
      key: "52 Week High/Low",
      value:
        "₹ " +
        stockData.value.mainSection.Wk52High +
        " / " +
        stockData.value.mainSection.Wk52Low,
      //Add tooltip
      tooltip: "",
    },
  ];

  const quarterData =
    stockData.value && stockData.value.quarterPerformanceData
      ? stockData.value.quarterPerformanceData
      : {};
  const qpMonths = Object.keys(quarterData);
  if (qpMonths.length === 0) {
    quarterlyPerformance.value = { columns: [], data: [] };
  } else {
    const firstMonth = qpMonths[0];
    const qpMetrics = Object.keys(quarterData[firstMonth] || {});
    const qpRows = qpMetrics.map((metric) => {
      const row = { metric };
      qpMonths.forEach((month) => {
        row[month] = quarterData[month]
          ? quarterData[month][metric]
          : undefined;
      });
      return row;
    });
    const qpFilteredRows = qpRows.filter((row) =>
      qpMonths.some((month) => {
        const v = row[month];
        return v !== undefined && v !== null && Number(v) !== 0;
      })
    );
    quarterlyPerformance.value = {
      columns: qpMonths,
      data: qpFilteredRows,
    };
  }

  const plMonths = Object.keys(stockData.value.profitAndLossData || {});
  const plMetrics = Object.keys(stockData.value.profitAndLossData[plMonths[0]]);

  const plRows = plMetrics.map((metric) => {
    const row = { metric };
    plMonths.forEach((month) => {
      row[month] = stockData.value.profitAndLossData[month][metric];
    });
    return row;
  });

  profitLoss.value = {
    columns: plMonths,
    data: plRows,
  };

  const bsMonths = Object.keys(stockData.value.balanceSheetData || {});
  const bsMetrics = Object.keys(stockData.value.balanceSheetData[bsMonths[0]]);

  const bsRows = bsMetrics.map((metric) => {
    const row = { metric };
    bsMonths.forEach((month) => {
      row[month] = stockData.value.balanceSheetData[month][metric];
    });
    return row;
  });

  balanceSheet.value = {
    columns: bsMonths,
    data: bsRows,
  };

  const cfMonths = Object.keys(stockData.value.cashFlowData || {});
  const cfMetrics = Object.keys(stockData.value.cashFlowData[cfMonths[0]]);

  const cfRows = cfMetrics.map((metric) => {
    const row = { metric };
    cfMonths.forEach((month) => {
      row[month] = stockData.value.cashFlowData[month][metric];
    });
    return row;
  });

  cashFlows.value = {
    columns: cfMonths,
    data: cfRows,
  };

  const spLabels = Object.keys(stockData.value.shareholdingPattern || {});
  const spValues = spLabels.map((label) =>
    stockData.value.shareholdingPattern[label].split("%")[0].trim()
  );
  shareholdingPattern.value = {
    labels: spLabels,
    datasets: [
      {
        data: spValues,
        backgroundColor: chartColors.slice(0, spLabels.length),
      },
    ],
  };

  news.value =
    stockData.value.news.map((item) => ({
      newsHeadline: item.title,
      newsURL: item.url,
    })) || [];
});

watch(
  () => props.isStockDialogOpen,
  (newVal) => {
    dialogVisible.value = newVal;
  }
);

watch(dialogVisible, (newVal) => {
  emit("update:isStockDialogOpen", newVal);
});

onMounted(() => {
  loadStockData();
});
</script>

<template>
  <Dialog
    modal
    v-model:visible="dialogVisible"
    :closable="false"
    :dismissable-mask="!dataLoading"
  >
    <template #header>
      <div
        class="d-flex align-items-center position-relative w-100 p-20 dialog-header"
      >
        <span class="text-center text-primary w-100 font-20 font-weight-700">{{
          stock.stockName
        }}</span>
        <i
          @click="emit('update:isStockDialogOpen', false)"
          class="pi pi-times bg-error text-white p-5 br-5 position-absolute r-0 mr-10 cursor-pointer"
        />
      </div>
    </template>
    <div v-if="dataLoading" class="position-relative loader-container">
      <AnLoader text="Stock data loading..." />
    </div>
    <div v-else-if="loadError" class="error-container">
      <div class="error-content">
        <i class="pi pi-exclamation-triangle error-icon"></i>
        <h3 class="error-title">Data Not Available</h3>
        <p class="error-message">
          We couldn't load the data for this stock. This could be due to:
        </p>
        <ul class="error-reasons">
          <li>Data not yet available in our system</li>
          <li>Temporary server issue</li>
          <li>Invalid stock identifier</li>
        </ul>
        <p class="error-suggestion">
          Please raise a support ticket and our team will investigate and resolve this issue.
        </p>
        <AnButton
          label="Ask for Help"
          icon="pi pi-headphones"
          variant="primary"
          @click="navigateToSupport"
          class="support-button"
        />
      </div>
    </div>
    <div v-else-if="stockData" class="ph-30 pb-30">
      <div class="d-grid input-grid gap-15 font-20 mt-30">
        <div class="d-flex gap-10" v-for="item in generalData">
          <span class="font-weight-600">{{ item.key }}:</span>
          <span class="font-weight-400">
            {{ item.value }}
            <i
              v-if="item.tooltip && item.key === 'Expense Ratio'"
              v-tooltip.right="{
                value: item.tooltip,
                class: 'left-arrow short-width',
              }"
              class="pi pi-info-circle font-15"
            />
            <i
              v-else-if="item.tooltip && item.key === 'Benchmark TRI 1 Year'"
              v-tooltip.left="{
                value: item.tooltip,
                class: 'right-arrow short-width',
              }"
              class="pi pi-info-circle font-15"
            />
          </span>
        </div>
      </div>
      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600">About</span>
        <span class="h-divider mt-5" />
        <span class="mt-20">
          {{ isExpanded ? stockData?.aboutCompany : truncatedText }}
          <button
            v-if="showToggle"
            class="read-more-btn"
            @click="isExpanded = !isExpanded"
          >
            {{ isExpanded ? "Read less" : "Read more" }}
          </button>
        </span>
      </div>

      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600">Quarterly Results</span>
        <span class="h-divider mt-5" />
      </div>
      <DataTable
        :value="quarterlyPerformance.data"
        class="mt-20"
        :pt="{
          headerRow: { class: 'font-weight-500' },
        }"
      >
        <Column
          field="metric"
          header=""
          frozen
          class="font-weight-600 text-primary"
        />

        <Column
          v-for="month in quarterlyPerformance.columns"
          :key="month"
          :field="month"
          :header="month"
          :body="
            (data) =>
              data[month]?.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })
          "
        />
      </DataTable>

      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600">Profit & Loss</span>
        <span class="h-divider mt-5" />
      </div>
      <DataTable
        :value="profitLoss.data"
        class="mt-20"
        :pt="{
          headerRow: { class: 'font-weight-500' },
        }"
      >
        <Column
          field="metric"
          header=""
          frozen
          class="font-weight-600 text-primary"
        />

        <Column
          v-for="month in profitLoss.columns"
          :key="month"
          :field="month"
          :header="month"
          :body="
            (data) =>
              data[month]?.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })
          "
        />
      </DataTable>

      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600">Balance sheet</span>
        <span class="h-divider mt-5" />
      </div>
      <DataTable
        :value="balanceSheet.data"
        class="mt-20"
        :pt="{
          headerRow: { class: 'font-weight-500' },
        }"
      >
        <Column
          field="metric"
          header=""
          frozen
          class="font-weight-600 text-primary"
        />

        <Column
          v-for="month in balanceSheet.columns"
          :key="month"
          :field="month"
          :header="month"
          :body="
            (data) =>
              data[month]?.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })
          "
        />
      </DataTable>

      <div class="d-flex flex-direction-column mt-30">
        <span class="font-20 font-weight-600">Cash Flows</span>
        <span class="h-divider mt-5" />
      </div>
      <DataTable
        :value="cashFlows.data"
        class="mt-20"
        :pt="{
          headerRow: { class: 'font-weight-500' },
        }"
      >
        <Column
          field="metric"
          header=""
          frozen
          class="font-weight-600 text-primary"
        />

        <Column
          v-for="month in cashFlows.columns"
          :key="month"
          :field="month"
          :header="month"
          :body="
            (data) =>
              data[month]?.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })
          "
        />
      </DataTable>

      <div class="d-flex flex-direction-column mt-30">
        <span class="font-weight-500 bg-primary text-center p-10 text-white">
          - - - - - Shareholding Pattern - - - - -</span
        >
        <div class="d-flex bg-light pv-20">
          <Chart
            type="pie"
            :data="shareholdingPattern"
            :options="pieChartOptions"
            class="d-flex flex-1 justify-center align-items-center"
            :height="170"
            :width="170"
          />
          <div class="flex-1 d-flex gap-10 h-max flex-wrap">
            <Chip
              v-for="(label, index) in shareholdingPattern.labels"
              :key="index"
              class="bg-primary p-10 br-10 font-weight-500"
            >
              <span
                class="p-5 br-round mr-5"
                :style="{
                  backgroundColor:
                    shareholdingPattern.datasets[0].backgroundColor?.[index] ||
                    chartColors[index % chartColors.length],
                }"
              ></span>
              <span class="text-white"
                >{{ label }}:
                {{ shareholdingPattern.datasets[0].data[index] }}%</span
              >
            </Chip>
          </div>
        </div>
      </div>
      <NewsSection :news="news" class="mt-30" />

      <div class="d-flex flex-direction-column mt-40">
        <span class="font-20 font-weight-600">Price & P/E Time-Series</span>
        <span class="h-divider mt-5" />
        <div class="d-flex justify-content-between align-items-center mt-10">
          <div class="d-flex gap-10">
            <span
              v-for="option in timeframeOptions"
              :key="option"
              @click="timeframe = option"
              class="timeframe-option"
              :class="{ active: timeframe === option }"
            >
              {{ option }}
            </span>
          </div>
        </div>
        <div class="chart-container mt-20" style="height: 300px">
          <Chart
            type="line"
            :data="pricePEChartData"
            :options="pricePEChartOptions"
            class="w-100 h-100"
          />
        </div>
      </div>
    </div>
    <template #footer>
      <div class="d-flex justify-center w-100 p-10">
        <AnButton
          label="Back"
          variant="primary"
          :is-disabled="dataLoading"
          @click="emit('update:isStockDialogOpen', false)"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
.loader-container {
  height: 400px;
}

.error-container {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.error-content {
  text-align: center;
  max-width: 500px;
}

.error-icon {
  font-size: 4rem;
  color: #f59e0b;
  margin-bottom: 20px;
}

.error-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0 0 16px 0;
}

.error-message {
  font-size: 1rem;
  color: var(--an-text-secondary);
  margin: 0 0 12px 0;
  line-height: 1.5;
}

.error-reasons {
  text-align: left;
  margin: 0 auto 20px;
  padding-left: 20px;
  max-width: 350px;
  color: var(--an-text-secondary);
  font-size: 0.9rem;
}

.error-reasons li {
  margin-bottom: 8px;
  line-height: 1.4;
}

.error-suggestion {
  font-size: 0.95rem;
  color: var(--an-text-primary);
  font-weight: 600;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.support-button {
  min-width: 200px;
}

.dialog-header {
  border-bottom: 2px solid;
  border-bottom-color: var(--an-primary);
}

.input-grid {
  grid-template-columns: repeat(3, 1fr);
}

.legend {
  border: 1px solid var(--an-gray1);
}

.read-more-btn {
  background: none;
  border: none;
  color: var(--an-primary);
  font-weight: 600;
  cursor: pointer;
  margin-left: 5px;
  padding: 0;
}
.read-more-btn:hover {
  text-decoration: underline;
}

.timeframe-option {
  padding: 8px 12px;
  border: 1px solid var(--an-primary);
  border-radius: 16px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-weight: 500;
  color: var(--an-text-primary);
}

.timeframe-option:hover {
  background-color: rgba(59, 130, 246, 0.1);
}

.timeframe-option.active {
  background-color: var(--an-primary);
  color: white;
}
</style>
